import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'
import type { Choice, TablePage, UserEventRow, UsersTableRequest } from './settingsUsers'
import { usersTableParams } from './settingsUsers'

/**
 * Settings -> My Account (`settings/user_accounts`), calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/User_accounts.php`:
 *
 *   index                    -> GET  /settings/user_accounts          (bara)
 *   my_account + personal_info + refreshUserSidebar
 *                            -> GET  /settings/user_accounts/me
 *   save_personal_info       -> POST /settings/user_accounts/me
 *   change_avatar            -> POST /settings/user_accounts/me/avatar
 *   (Remove Avatar)          -> POST /settings/user_accounts/me/avatar/remove
 *   edit_password            -> POST /settings/user_accounts/me/password
 *   privacy_settings         -> GET/POST /settings/user_accounts/me/privacy
 *   account_events           -> GET  /settings/user_accounts/me/events
 *   out_of_office            -> GET  /settings/user_accounts/out_of_office
 *   oofNewRecordForm         -> GET  /settings/user_accounts/out_of_office/new
 *   save_out_of_office       -> POST /settings/user_accounts/out_of_office
 *   edit_oof_form            -> GET  /settings/user_accounts/out_of_office/{id}
 *   edit_out_of_office       -> POST /settings/user_accounts/out_of_office/{id}
 *
 * Ecranul e AL OMULUI DESPRE EL: nicio cerere nu poarta un ID de om, il ia
 * serverul din token.
 */

export type MyAccountTab = { id: string; label: string; icon: string }

export type MyAccountShell = {
  pageTitle: string
  moduleUrl: string
  tabs: MyAccountTab[]
  accountTabs: { id: string; label: string }[]
}

export const MY_ACCOUNT_TABS = ['my_account', 'out_of_office'] as const

export type MyAccountTabId = (typeof MY_ACCOUNT_TABS)[number]

export function isMyAccountTab(value: string): value is MyAccountTabId {
  return (MY_ACCOUNT_TABS as readonly string[]).includes(value)
}

export const myAccountShellQuery = queryOptions({
  queryKey: ['settings', 'my_account', 'shell'],
  queryFn: () => apiGet<MyAccountShell>('/settings/user_accounts'),
})

/* -------------------------------------------------------------------------
 * My Account
 * ---------------------------------------------------------------------- */

export type MyProfile = {
  user: {
    id: number
    firstname: string
    lastname: string
    email: string
    mobileNumber: string
    workNumber: string
    occupation: string
    address: string
    companyId: number
    /** Poza de pe serverul de descarcari; '' cand nu e configurat. */
    avatarUrl: string
  }
  companies: Choice[]
  signatureTemplateId: number
  /** Corpul semnaturii omului - butonul "Signature" din Personal Info. */
  signatureBody: string
  passwordHint: string
}

export const myProfileQuery = queryOptions({
  queryKey: ['settings', 'my_account', 'me'],
  queryFn: () => apiGet<MyProfile>('/settings/user_accounts/me'),
})

export function savePersonalInfo(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>('/settings/user_accounts/me', fields)
}

export function saveMyAvatar(form: FormData) {
  return apiPostForm<{ saved: boolean; file: string }>('/settings/user_accounts/me/avatar', form)
}

export function removeMyAvatar() {
  return apiPost<{ saved: boolean }>('/settings/user_accounts/me/avatar/remove', {})
}

export function changeMyPassword(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>('/settings/user_accounts/me/password', fields)
}

export const myPrivacyQuery = queryOptions({
  queryKey: ['settings', 'my_account', 'privacy'],
  queryFn: () => apiGet<{ timezones: Choice[]; timezone: number }>('/settings/user_accounts/me/privacy'),
})

export function saveMyPrivacy(timezone: number) {
  return apiPost<{ saved: boolean }>('/settings/user_accounts/me/privacy', { timezone })
}

export function myEventsQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'my_account', 'events', r],
    queryFn: () =>
      apiGet<TablePage<UserEventRow>>(`/settings/user_accounts/me/events?${usersTableParams(r).toString()}`),
  })
}

/* -------------------------------------------------------------------------
 * Out Of Office
 * ---------------------------------------------------------------------- */

export type OutOfOfficeRow = {
  ID: number
  from: string
  to: string
  details: string | null
  my_tickets: number
  my_companies: number
  user: string
  substitute: string
}

export function outOfOfficeQuery(r: UsersTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'my_account', 'oof', r],
    queryFn: () =>
      apiGet<TablePage<OutOfOfficeRow>>(`/settings/user_accounts/out_of_office?${usersTableParams(r).toString()}`),
  })
}

export type OutOfOfficeChoices = {
  admins: Choice[]
  departments: Choice[]
  /** Textul implicit al instiintarii, cu `[period]`, `[substitute]`, `[contact]`. */
  message: string
}

export const outOfOfficeFormQuery = queryOptions({
  queryKey: ['settings', 'my_account', 'oof', 'new'],
  queryFn: () => apiGet<OutOfOfficeChoices>('/settings/user_accounts/out_of_office/new'),
})

export type OutOfOfficeRecord = {
  id: number
  from: string
  to: string
  details: string
  myTickets: boolean
  myCompanies: boolean
  substitute: number
}

export function outOfOfficeRecordQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'my_account', 'oof', id],
    queryFn: () =>
      apiGet<{ record: OutOfOfficeRecord; closed: boolean; admins: Choice[]; departments: Choice[] }>(
        `/settings/user_accounts/out_of_office/${id}`,
      ),
  })
}

/** Raspunde si cu `notified`: cate instiintari au plecat cu adevarat. */
export function createOutOfOffice(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number; notified: number }>('/settings/user_accounts/out_of_office', fields)
}

export function saveOutOfOffice(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean }>(`/settings/user_accounts/out_of_office/${id}`, fields)
}
